import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CityBox from '@components/Caja';
import Boton from '@components/Boton';
import BotonBold from '@components/BotonBold';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { verUserApi } from "@api/wp/users/userApi";
import { getAllZonasApi } from "@api/wp/zonas/zonasApi";
import { verCitasApi } from "@api/wp/agenda/citas/citasApi";
import { viewEmpresasApi } from "@api/wp/empresas/empresasApi";
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { LAYOUT_COMPACT_MAX_PX } from '@utils/breakpoints';
import { horaCitaAMinutosDesdeMedianoche } from '@utils/citaHoraParse';
import { MEMBERSHIP_URL } from '@utils/const';

/** Ancho mínimo columna Hora (tabla escritorio citas pendientes). */
import LoadingOverlay from '@components/LoadingOverlay';
import { useCita } from '@providers/citaContext';
import { colors } from '@styles/colors';
import '@styles/modernTable.css';
import { DataCard, DataCardList } from '@components/DataCard';

/** order_id utilizable para pago Bold (no null, vacío, '-' ni '0'). */
function orderIdValidoParaBold(c) {
  const oid = c?.order_id;
  if (oid == null) return false;
  const s = String(oid).trim();
  return s !== '' && s !== '-' && s !== '0';
}

function citaEstaCancelada(c) {
  return String(c?.estado ?? '').trim().toLowerCase() === 'cancelada';
}

function citaMetodoEsBold(c) {
  return String(c?.metodo ?? '').trim().toLowerCase() === 'bold';
}

/** Pago Bold confirmado: oficina_citas.metodo = bold y oficina_bold_response.type vía idResBold. */
function boldVentaAprobada(c) {
  return citaMetodoEsBold(c) && String(c?.bold_response_type ?? '').trim() === 'SALE_APPROVED';
}

/** Cita con flujo Bold pendiente: order válido, sin SALE_APPROVED, no cancelada y estado Pendiente. */
function esBoldPendientePorPagar(c) {
  if (citaEstaCancelada(c)) return false;
  if (!citaMetodoEsBold(c)) return false;
  if (!orderIdValidoParaBold(c)) return false;
  if (boldVentaAprobada(c)) return false;
  return String(c?.estado ?? '').trim().toLowerCase() === 'pendiente';
}

/** Primero Bold pendiente por pagar; luego el resto; dentro de cada grupo por fecha y hora. */
function ordenarCitasParaCiudad(lista) {
  return [...lista].sort((a, b) => {
    const pa = esBoldPendientePorPagar(a) ? 0 : 1;
    const pb = esBoldPendientePorPagar(b) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    const fa = a.fecha ? String(a.fecha).slice(0, 10) : '';
    const fb = b.fecha ? String(b.fecha).slice(0, 10) : '';
    if (fa !== fb) return fa.localeCompare(fb);
    const ma = horaCitaAMinutosDesdeMedianoche(a.hora);
    const mb = horaCitaAMinutosDesdeMedianoche(b.hora);
    return ma - mb;
  });
}

function Ciudad() {
  const navigate = useNavigate();
  const { setCita } = useCita();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(true);
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [citasPendientes, setCitasPendientes] = useState([]);
  const [loadingCitas, setLoadingCitas] = useState(false);
  const boldButtonRefs = React.useRef({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= LAYOUT_COMPACT_MAX_PX);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= LAYOUT_COMPACT_MAX_PX);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Variables Bold
  const [integritySignature, setIntegritySignature] = useState('');
  const [clave_bold, setClave_bold] = useState("");
  const [key_bold, setKeyBold] = useState("");
  const modoRevBold = 1; // Ajustar según entorno (0: Prod, 1: Dev)

  // Validación: si no hay sesión en localStorage o es "0", redirigir a membresía
  useEffect(() => {
    const idUsu = localStorage.getItem('idUsu');
    if (!idUsu || idUsu === '0') {
      window.location.href = MEMBERSHIP_URL;
      return;
    }
  }, []);

  useEffect(() => {
    const nombreCompleto = localStorage.getItem('nombreCompleto');
    setNombreCompleto(nombreCompleto || '');
    cargarZonas();
    cargarCitasPendientes();
    cargarClavesBold();
  }, []);

  // Función para cargar claves de Bold
  const cargarClavesBold = async () => {
    try {
      // Nota: Asumimos una empresa por defecto o buscamos una genérica si no hay contexto específico.
      // En confirmar.jsx se busca por empresaSeleccionada. Aquí podríamos necesitar una lógica similar
      // o cargar las credenciales de una empresa "default" si aplica, o iterar sobre las citas para obtener sus empresas.
      // Por simplicidad y siguiendo el patrón, intentaremos cargar claves generales o iterar luego.
      // Sin embargo, para que funcione el botón Bold, necesitamos la apiKey y la clave para generar el hash.
      // Vamos a intentar cargar las de una empresa 'Previsalud' o similar, o dejar que se carguen dinámicamente.
      // Dado que el usuario pidió "realizar el pago enviando el order_id", asumiremos que la configuración
      // ya existe o se debe obtener. 
      // IMPORTANTE: El hash se genera con la CLAVE PRIVADA que no debe estar en el front, pero aquí 
      // estamos replicando la lógica de confirmar.jsx donde se obtiene de viewEmpresasApi.

      // Para este caso, vamos a dejar que el botón maneje su lógica o preparar las claves.
      // Si cada cita tiene una empresa distinta, esto se complica.
      // Asumiremos que todas las citas usan la misma configuración de pasarela por ahora o 
      // implementaremos la carga por cita si es necesario.

      // Revisando confirmar.jsx: usa viewEmpresasApi({ nombre_empresa: nombre })
      // Aquí no tenemos una sola empresa. 
      // Vamos a cargar las credenciales de la primera cita o iterar si es necesario.
      // Por ahora, cargaremos una genérica o la primera que encontremos.
    } catch (error) {
      console.error("Error cargando claves Bold", error);
    }
  };

  // Función auxiliar para generar Hash (copiada de confirmar.jsx)
  async function generateHash(cadena) {
    const encodedText = new TextEncoder().encode(cadena);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedText);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  // Manejar el pago de una cita específica
  const handlePagarCita = async (cita) => {
    try {
      if (!cita.order_id || cita.order_id === '-' || cita.order_id === '0') {
        alert("Esta cita no tiene un Order ID válido para pagar con Bold.");
        return;
      }

      // Necesitamos la empresa de la cita para obtener las credenciales y generar el hash
      const empresaNombre = cita.empresa || cita.nombre_empresa || "";
      let currentKey = key_bold;
      let currentClave = clave_bold;

      if (!currentKey || !currentClave) {
        const resp = await viewEmpresasApi({ nombre_empresa: empresaNombre });
        const lista = resp?.result || [];
        if (lista.length > 0) {
          if (modoRevBold === 0) {
            currentKey = lista[0]?.key_bold_prod;
            currentClave = lista[0]?.clave_bold_prod;
          } else {
            currentKey = lista[0]?.key_bold_dev;
            currentClave = lista[0]?.clave_bold_dev;
          }
        }
      }

      if (!currentKey || !currentClave) {
        alert("No se pudieron obtener las credenciales de pago para esta empresa.");
        return;
      }

      // Generar firma de integridad
      const amount = String(Number(cita.valor || 0)); // Asegurarse de tener el valor
      const currency = 'COP';
      const orderId = cita.order_id;
      const cadenaConcatenada = `${orderId}${amount}${currency}${currentClave}`;
      const hash = await generateHash(cadenaConcatenada);

      // Configurar el botón Bold dinámicamente y abrirlo
      // Como BotonBold espera props, y aquí tenemos multiples filas, 
      // una opción es tener un componente BotonBold por fila o uno global invisible.
      // Vamos a usar la ref de la fila específica.

      const btnRef = boldButtonRefs.current[cita.id];
      if (btnRef) {
        // Actualizamos props imperativamente o forzamos el render con estado?
        // BotonBold no tiene método para actualizar props, usa las que se le pasan.
        // Pero `openCheckout` usa los valores pasados al inicializar `window.BoldCheckout`.
        // El componente BotonBold crea la instancia AL LLAMAR a openCheckout?
        // Revisando BotonBold.jsx: 
        // useImperativeHandle(ref, () => ({ openCheckout: () => { new window.BoldCheckout({...props}) ... } }))
        // ¡Sí! Usa las props actuales al momento de ejecutar openCheckout.
        // Entonces necesitamos que el componente BotonBold de esa fila tenga las props correctas (hash, key, etc).

        // PROBLEMA: El estado `integritySignature` es global en este componente, pero tenemos N citas.
        // SOLUCIÓN: Calcular el hash y pasarlo directamente al componente de la fila NO funcionará 
        // si usamos un solo estado. Necesitamos un estado por fila o un componente wrapper.
        // O mejor: Crear un componente `FilaCita` que maneje su propia lógica de Bold.
        // O más simple: Actualizar el estado de "Cita a Pagar" con su hash, esperar el re-render y luego abrir.

        // Vamos a usar el enfoque de: Setear cita activa para pago -> Calcular Hash -> Renderizar Boton -> Abrir.
        // Pero el usuario quiere ver el botón en la columna.
        // Así que cada fila tendrá su BotonBold.
        // Para simplificar, pasaremos el hash calculado al BotonBold de la fila si ya lo tenemos, 
        // o lo calculamos on-the-fly? No, BotonBold necesita el hash en props.

        // Vamos a hacer que cada fila sea un sub-componente o manejar un array de hashes.
        // Dado la complejidad, vamos a hacer un componente `CitaRow` interno o externo.
      }
    } catch (e) {
      console.error("Error al iniciar pago:", e);
    }
  };

  // Componente interno para fila de cita que maneja su propio estado de Bold
  const FilaCita = ({ cita }) => {
    const [integritySignature, setIntegritySignature] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [loadingBold, setLoadingBold] = useState(false);
    const boldBtnRef = React.useRef(null);
    const modoRevBold = 0; // 0: Prod, 1: Dev - Ajustar según entorno global o prop

    const iniciarPago = async () => {
      try {
        if (!cita.order_id || cita.order_id === '-' || cita.order_id === '0') {
          alert("Esta cita no tiene un Order ID válido.");
          return;
        }
        setLoadingBold(true);

        // 1. Obtener credenciales de la empresa
        const empresaNombre = cita.empresa || cita.nombre_empresa || "";
        let currentKey = "";
        let currentClave = "";

        const resp = await viewEmpresasApi({ nombre_empresa: empresaNombre });
        const lista = resp?.result || [];
        if (lista.length > 0) {
          if (modoRevBold === 0) {
            currentKey = lista[0]?.key_bold_prod;
            currentClave = lista[0]?.clave_bold_prod;
          } else {
            currentKey = lista[0]?.key_bold_dev;
            currentClave = lista[0]?.clave_bold_dev;
          }
        }

        if (!currentKey || !currentClave) {
          alert("No se encontraron credenciales de pago para la empresa asignada.");
          setLoadingBold(false);
          return;
        }
        setApiKey(currentKey);

        // 2. Generar Hash
        const amount = String(Number(cita.valor || 0));
        const currency = 'COP';
        const orderId = cita.order_id;
        const cadenaConcatenada = `${orderId}${amount}${currency}${currentClave}`;
        const hash = await generateHash(cadenaConcatenada);
        setIntegritySignature(hash);

        // 3. Abrir Checkout (esperar a que el estado se actualice y luego abrir)
        // Usamos setTimeout para asegurar que el re-render con el nuevo hash ocurra
        setTimeout(() => {
          if (boldBtnRef.current && boldBtnRef.current.openCheckout) {
            boldBtnRef.current.openCheckout();
          }
          setLoadingBold(false);
        }, 100);

      } catch (error) {
        console.error("Error iniciando pago Bold:", error);
        setLoadingBold(false);
      }
    };

    const cancelada = citaEstaCancelada(cita);
    const metodoVentanilla = String(cita?.metodo ?? '').trim().toLowerCase() === 'ventanilla';
    const otrosMetodoPagado = !citaMetodoEsBold(cita) && Number(cita?.idResBold ?? 0) > 0;
    const puedePagar = cita.order_id && cita.order_id !== '-' && cita.order_id !== '0';
    const aprobadoBold = boldVentaAprobada(cita);
    const mostrarBotonPago =
      !cancelada &&
      !metodoVentanilla &&
      puedePagar &&
      !aprobadoBold &&
      !otrosMetodoPagado;

    const formatFecha = (s) => {
      if (!s) return '';
      const parts = String(s).split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        return `${d}/${m}/${y}`;
      }
      return String(s);
    };

    const sinFecha = !cita.fecha;
    const sinHora = !cita.hora || String(cita.hora).trim() === '';
    const estiloRecuadroFechaHora = (placeholder, esHora = false) => ({
      display: 'inline-block',
      fontWeight: placeholder ? 600 : 700,
      fontFamily: 'Inter, sans-serif',
      backgroundColor: placeholder ? colors.gray200 : '#FFF3E8',
      color: placeholder ? colors.gray600 : '#9E4410',
      border: `1px solid ${placeholder ? colors.borderLight : '#F0C9A8'}`,
      borderRadius: isMobile ? 12 : 10,
      padding: isMobile ? '8px 14px' : '0.55vh 1.25vh',
      fontSize: esHora
        ? isMobile
          ? 'clamp(11px, 2.8vw, 14px)'
          : 'clamp(10px, 1.1vh, 12px)'
        : isMobile
          ? 'clamp(14px, 3.6vw, 17px)'
          : 'clamp(13px, 1.4vh, 15px)',
      lineHeight: 1.25,
      boxShadow: placeholder ? 'none' : '0 1px 4px rgba(180, 95, 35, 0.12)',
      letterSpacing: '0.02em',
    });
    const valorFechaDestacado = (
      <span style={estiloRecuadroFechaHora(sinFecha)}>
        {sinFecha ? 'No especificada' : formatFecha(cita.fecha)}
      </span>
    );
    const valorHoraDestacada = (
      <span style={estiloRecuadroFechaHora(sinHora, true)}>
        {sinHora ? 'No especificada' : String(cita.hora).trim()}
      </span>
    );

    const renderCeldaPago = (alineacionCentro = true) => (
      <div style={{ textAlign: alineacionCentro ? 'center' : 'left', width: '100%' }}>
        {mostrarBotonPago && puedePagar ? (
          <>
            <div style={{ display: 'none' }}>
              <BotonBold
                ref={boldBtnRef}
                integritySignature={integritySignature}
                onClick={() => { }}
                apiKey={apiKey}
                orderId={cita.order_id}
                amount={String(Number(cita.valor || 0))}
                description={cita.nombre_espe || 'Pago Cita Médica'}
              />
            </div>

            <div
              onClick={loadingBold ? undefined : iniciarPago}
              style={{
                cursor: loadingBold ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5vh',
                backgroundColor: colors.verdeClaro,
                padding: '0.8vh 1.4vh',
                borderRadius: '1vh',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginTop: isMobile ? '0.4vh' : 0,
              }}
            >
              {loadingBold ? (
                <span style={{ fontSize: '1.4vh' }}>Cargando...</span>
              ) : (
                <>
                  <span style={{ fontSize: '1.5vh', fontWeight: 600 }}>Realizar pago</span>
                  <img src="/bold-icon.png" alt="" style={{ height: '2vh', display: 'none' }} />
                  <PaymentIcon sx={{ fontSize: '2.5vh', color: colors.primaryText }} />
                </>
              )}
            </div>
          </>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh', flexWrap: 'wrap' }} title="Pago en ventanilla">
            {aprobadoBold || otrosMetodoPagado ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh', flexWrap: 'wrap' }} title="Pago en linea">
                <CreditCardIcon style={{ color: colors.success, fontSize: isMobile ? '3.2vh' : '4.0vh' }} />
                {aprobadoBold && (
                  <>
                    <span style={{ fontWeight: 700, color: colors.success, fontSize: isMobile ? 'clamp(13px, 3.2vw, 15px)' : '1.35vh', fontFamily: 'Inter, sans-serif' }}>
                      PAGADA
                    </span>
                  </>
                )}
              </span>
            ) : (
              <>
                <StorefrontIcon style={{ color: colors.gray600, fontSize: isMobile ? '3.2vh' : '4.0vh' }} />
              </>
            )}
          </span>
        )}
      </div>
    );

    if (isMobile) {
      return (
        <DataCard
          rows={[
            { label: 'Servicio', value: cita.nombre_espe || 'No especificado' },
            { label: 'Sede', value: cita.nombre_sede || 'No especificada' },
            { label: 'Médico', value: cita.nombreMedico || 'No especificado' },
            { label: 'Fecha', value: valorFechaDestacado },
            { label: 'Hora', value: valorHoraDestacada },
            {
              label: <span style={{ whiteSpace: 'nowrap' }}>Estado Cita</span>,
              value: <span>ASIGNADA</span>,
            },
            { label: 'Realizar pago', value: renderCeldaPago(false), layout: 'stack' },
          ]}
        />
      );
    }

    return (
      <tr>
        <td>{cita.nombre_espe || 'No especificado'}</td>
        <td>{cita.nombre_sede || 'No especificada'}</td>
        <td>{cita.nombreMedico || 'No especificado'}</td>
        <td>{valorFechaDestacado}</td>
        <td style={{ minWidth: '5rem', boxSizing: 'border-box' }}>{valorHoraDestacada}</td>
        <td style={{ textAlign: 'center' }}>ASIGNADA</td>
        <td style={{ textAlign: 'center' }}>{renderCeldaPago(true)}</td>
      </tr>
    );
  };

  const cargarCitasPendientes = async () => {
    try {
      setLoadingCitas(true);
      const idPaciente = localStorage.getItem('idUsu');
      const response = await verCitasApi({ id_paciente: idPaciente, proximosDias: 2 });
      if (response.statusCode === 200 && response.result) {
        const result = Array.isArray(response.result) ? response.result : [response.result];
        const merged = result.map((c) => ({
          ...c,
          nombre_espe: c.nombre_espe || c.nombre_espe_db || '',
        }));
        setCitasPendientes(ordenarCitasParaCiudad(merged));
      }
    } catch (error) {
      console.error('Error al cargar citas:', error);
    } finally {
      setLoadingCitas(false);
    }
  };

  const cargarZonas = async () => {
    try {
      const response = await getAllZonasApi();
      console.log('Zonas cargadas:', response.result);
      setZonas(response.result || []);
    } catch (error) {
      console.error('Error al cargar zonas:', error);
      // En caso de error, usar datos por defecto
      setZonas([
        { "id": "1", "nombre": "Atlantico" },
        { "id": "2", "nombre": "Norte de Santander" },
        { "id": "3", "nombre": "Cesar" },
      ]);
    } finally {
      setLoadingZonas(false);
    }
  };



  return (
    <div id="cajaPrincipal"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoadingOverlay visible={loadingZonas} />
      <div id="avatar-central"
        style={{
          width: '9.36vh',
          height: '9.36vh',
          borderRadius: '4.8vh',
          transform: 'rotate(0deg)',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 1
        }}
      >
      </div>
      <div id="cajaBienvenido"
        style={{
          width: isMobile ? '85%' : '113.3vh',
          height: isMobile ? 'auto' : '17.5vh',
          minHeight: isMobile ? '16vh' : '17.5vh',
          padding: isMobile ? '3.5vh 2.5vh' : '0',
          borderRadius: isMobile ? '3vh' : '8.762vh',
          transform: 'rotate(0deg)',
          opacity: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: isMobile ? '3.0vh' : '3.0vh',
          fontFamily: 'Inter',
          backgroundColor: colors.verdeClaro,
          color: colors.gray700,
          position: 'relative',
          gap: isMobile ? '1.5vh' : '0'
        }}
      >
        <img
          src="/bienvenido.png"
          alt="Bienvenido"
          style={{
            width: isMobile ? '12vh' : '12vh',
            height: isMobile ? '10vh' : '10vh',
            objectFit: 'contain',
            position: isMobile ? 'static' : 'absolute',
            left: isMobile ? 'auto' : '4vh',
            top: isMobile ? 'auto' : '50%',
            transform: isMobile ? 'none' : 'translateY(-50%)'
          }}
        />
        <div id="textoBievenida" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '0.35vh' : '0.45vh',
          lineHeight: 1.05,
        }}>
          <div>¡Bienvenido!</div>
          <div style={{ fontWeight: 700 }}>{nombreCompleto}</div>
          <div>es hora de agendar tu cita.</div>
        </div>
      </div>

      <div id="cajaDonde"
        style={{
          width: isMobile ? '100%' : '50.0vh',
          height: 'auto',
          fontWeight: 700,
          transform: 'rotate(0deg)',
          opacity: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: isMobile ? '3.4vh' : '3.0vh',
          fontFamily: 'Inter',
          color: colors.primaryText,
          position: 'relative',
          marginTop: isMobile ? '3vh' : '4.0vh',
          padding: isMobile ? '0 2vh' : '0'
        }}
      >
        ¿Donde estas ubicado?
      </div>

      <div id="cajaSelec"
        style={{
          width: isMobile ? '90%' : '70.0vh',
          height: 'auto',
          transform: 'rotate(0deg)',
          opacity: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: isMobile ? '2.4vh' : '2.0vh',
          fontFamily: 'Inter',
          color: colors.gray700,
          marginTop: isMobile ? '1.5vh' : '0'
        }}
      >
        Selecciona tu departamento donde deseas el servicio
      </div>
      {/* Contenedor de ciudades */}
      <div id="cajCiudades" style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: isMobile ? '85%' : 'auto',
        gap: '1.5vh',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        justifyContent: 'center',
        alignItems: isMobile ? 'stretch' : 'center',
        marginTop: '2vh',
      }}>
        {loadingZonas ? (
          <div>Cargando zonas...</div>
        ) : (
          zonas.map((item, index) => (
            <CityBox
              key={index}
              label={item.nombre}
              isSelected={ciudadSeleccionada === item.id}
              onClick={() => setCiudadSeleccionada(item.id)}
              style={isMobile ? { height: '8.5vh', fontSize: '2.6vh' } : {}}
            />
          ))
        )}
      </div>
      <div id="cajaSiguiente"
        style={{
          width: isMobile ? '85%' : '75vh',
          height: 'auto',
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-end',
          position: 'relative',
          marginTop: '3vh'
        }}
      >
        {/* Botón debajo de las ciudades */}
        <Boton
          label="Continuar"
          siguiente
          onClick={() => {
            if (ciudadSeleccionada) {
              const ciudadObj = zonas.find(c => c.id === ciudadSeleccionada);
              setCita(prev => ({
                ...prev,
                ciudadId: ciudadSeleccionada,
                ciudadNombre: ciudadObj?.nombre || ''
              }));
              navigate('/servicios');
            } else {
              alert('Por favor selecciona una ciudad');
            }
          }}
        />
      </div>
      <div style={{
        marginTop: '1vh',
        width: isMobile ? 'calc(100% - 3vh)' : '80%',
        maxWidth: isMobile ? '100%' : '120vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingLeft: isMobile ? '1.5vh' : 0,
        paddingRight: isMobile ? '1.5vh' : 0,
        boxSizing: 'border-box',
        paddingBottom: '4vh'
      }}>
        <div style={{
          width: '100%',
          textAlign: 'left',
          marginBottom: '2vh',
          fontSize: isMobile ? '2.2vh' : '2.5vh',
          fontFamily: 'Inter',
          fontWeight: 700,
          color: colors.primaryText
        }}>
          Citas pendientes por pago
        </div>

        {loadingCitas ? (
          <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'Inter', color: colors.gray600 }}>
            Cargando citas pendientes...
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              overflowX: isMobile ? 'visible' : undefined,
            }}
          >
            {citasPendientes.length > 0 ? (
              isMobile ? (
                <DataCardList>
                  {citasPendientes.map((cita) => (
                    <FilaCita key={cita.id} cita={cita} />
                  ))}
                </DataCardList>
              ) : (
                <div className="modern-table-container">
                  <table id="tabla-citas-pendientes-ciudad" className="modern-table">
                    <thead>
                      <tr>
                        <th>Servicio</th>
                        <th>Sede</th>
                        <th>Médico</th>
                        <th>Fecha</th>
                        <th style={{ minWidth: '5rem', boxSizing: 'border-box' }}>Hora</th>
                        <th style={{ textAlign: 'center' }}>Estado&nbsp;Cita</th>
                        <th style={{ textAlign: 'center' }}>Estado Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {citasPendientes.map((cita) => (
                        <FilaCita key={cita.id} cita={cita} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div style={{
                padding: '3vh',
                textAlign: 'center',
                fontFamily: 'Inter',
                color: colors.gray600,
                backgroundColor: isMobile ? colors.white : undefined,
                borderRadius: '1.2vh',
                boxShadow: isMobile ? '0 0.4vh 1.2vh rgba(0,0,0,0.06)' : undefined,
              }}>
                No tienes citas pendientes por pago.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default Ciudad;